import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Carte, Chargement, Compteur, MessageErreur, Pagination, Pastille, PastilleSite } from '../ui/Primitives';
import { BarreSites } from '../ui/BarreSites';
import { BarreFiltres } from '../ui/BarreFiltres';
import { ChampFiltre, ChampRecherche } from '../ui/Onglets';
import { useToast } from '../ui/useToast';
import { usePerimetre } from '../../site/usePerimetre';
import { ErreurApi } from '../../lib/api';
import { useTaillePage } from '../../lib/useTaillePage';
import { useValeurDifferee } from '../../lib/useRecherche';
import {
  exporterPilotage,
  usePilotageFinanceurs,
  type DossierPilote,
  type FiltresPilotage,
  type StatutDossierFinance,
  type SyntheseFinanceur,
} from '../../lib/usePilotageFinanceurs';

type Filtres = Omit<FiltresPilotage, 'site'>;

const SANS_FILTRE: Filtres = { financeur: '', type: '', typeFacturation: '', statut: '', retard: '', recherche: '' };

/** « 2 à venir · 1 terminé » : les statuts autres qu'« en cours », dans l'ordre de l'API. */
const STATUTS_SECONDAIRES: { statut: StatutDossierFinance; singulier: string; pluriel: string }[] = [
  { statut: 'A_VENIR', singulier: 'à venir', pluriel: 'à venir' },
  { statut: 'TERMINE', singulier: 'terminé', pluriel: 'terminés' },
  { statut: 'ROMPU', singulier: 'rompu', pluriel: 'rompus' },
  { statut: 'DATES_A_PRECISER', singulier: 'dates à préciser', pluriel: 'dates à préciser' },
];

const CLASSE_TH = 'px-3 py-3 text-[11px] font-bold uppercase tracking-wider text-codialis-gray lg:px-4';
const CLASSE_TD = 'px-3 py-3 align-top lg:px-4';
const CLASSE_LIEN =
  'inline-flex min-h-[36px] items-center whitespace-nowrap text-xs font-semibold text-codialis-sky hover:underline [@media(pointer:coarse)]:min-h-[44px]';

function pluriel(nombre: number, singulier: string, plurielTexte = `${singulier}s`): string {
  return `${nombre} ${nombre > 1 ? plurielTexte : singulier}`;
}

function jourFrancais(iso: string): string {
  const [annee, mois, jour] = iso.slice(0, 10).split('-');
  return `${jour}/${mois}/${annee}`;
}

function autresStatuts(parStatut: Record<StatutDossierFinance, number>): string | null {
  const parties = STATUTS_SECONDAIRES.filter(({ statut }) => parStatut[statut] > 0).map(
    ({ statut, singulier, pluriel: plurielTexte }) =>
      `${parStatut[statut]} ${parStatut[statut] > 1 ? plurielTexte : singulier}`,
  );
  return parties.length > 0 ? parties.join(' · ') : null;
}

/** « 2 factures, depuis 12 jours » : le détail d'un retard de paiement. */
function detailRetard(retard: SyntheseFinanceur['enRetard']): string {
  const depuis = retard.joursMax === null ? '' : `, depuis ${pluriel(retard.joursMax, 'jour')}`;
  return `${pluriel(retard.nombre, 'facture')}${depuis}`;
}

/** « réglé en 24 jours en moyenne » ; à zéro, le jour même de l'émission. */
function texteDelai(jours: number): string {
  return jours === 0 ? 'réglé le jour de l’émission, en moyenne' : `réglé en ${pluriel(jours, 'jour')} en moyenne`;
}

/**
 * Pilotage par financeur (phase 5, TF-428) : pour chaque financeur — OPCO,
 * France Travail, autre, employeur en paiement direct —, ses dossiers, ses
 * références de prise en charge (DK), ce qu'il doit et ses retards ; puis les
 * dossiers retenus. Filtres par financeur, type de financeur, type de
 * facturation (TF-465), statut du dossier et retard,
 * et export Excel de ce qui est affiché.
 *
 * Tout vient de `/api/financements/pilotage` (`domain/pilotage-financeurs.ts`) :
 * l'écran ne recalcule rien. Les dossiers sont ceux de « Prises en charge »,
 * les factures celles de « Factures ».
 */
export function PilotageFinanceurs(): JSX.Element {
  const { site } = usePerimetre();
  const toast = useToast();
  const [filtres, setFiltres] = useState<Filtres>(SANS_FILTRE);
  const recherche = useValeurDifferee(filtres.recherche.trim(), 300);
  const parametres: FiltresPilotage = { site, ...filtres, recherche };
  const { data, isLoading, error } = usePilotageFinanceurs(parametres);
  const [page, setPage] = useState(1);
  const [taillePage, setTaillePage] = useTaillePage();
  const [exportEnCours, setExportEnCours] = useState(false);
  const refDossiers = useRef<HTMLDivElement>(null);

  // Un filtre, une antenne ou une taille de page qui change ramène à la première page.
  useEffect(
    () => setPage(1),
    [site, recherche, filtres.financeur, filtres.type, filtres.typeFacturation, filtres.statut, filtres.retard, taillePage],
  );

  const changer = <C extends keyof Filtres>(cle: C, valeur: Filtres[C]): void =>
    setFiltres((avant) => ({ ...avant, [cle]: valeur }));
  const filtreActif = JSON.stringify(filtres) !== JSON.stringify(SANS_FILTRE);

  const dossiers = useMemo(() => data?.dossiers ?? [], [data?.dossiers]);
  const pages = Math.max(1, Math.ceil(dossiers.length / taillePage));
  const affiches = useMemo(() => {
    const debut = (page - 1) * taillePage;
    return dossiers.slice(debut, debut + taillePage);
  }, [dossiers, page, taillePage]);

  const exporter = async (): Promise<void> => {
    setExportEnCours(true);
    try {
      const { lignes } = await exporterPilotage(parametres);
      toast(`Fichier exporté — ${pluriel(lignes, 'dossier')}`);
    } catch (erreur) {
      toast(erreur instanceof ErreurApi ? erreur.message : 'L’export n’a pas pu être produit.');
    } finally {
      setExportEnCours(false);
    }
  };

  const voirDossiers = (cle: string): void => {
    changer('financeur', cle);
    refDossiers.current?.scrollIntoView?.({ behavior: 'smooth', block: 'start' });
  };

  const total = data?.total;
  const vide = data !== undefined && data.perimetre.dossiers === 0;

  return (
    <>
      <BarreSites />

      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-4">
        <Compteur
          libelle="Dossiers en cours"
          valeur={total?.dossiers.parStatut.EN_COURS ?? '—'}
          detail={
            total
              ? [
                  `sur ${pluriel(total.dossiers.total, 'dossier')}`,
                  total.references.manquantes > 0
                    ? pluriel(total.references.manquantes, 'référence DK manquante', 'références DK manquantes')
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : undefined
          }
        />
        <Compteur
          libelle="Pris en charge"
          valeur={total ? (total.engage?.texte ?? '—') : '—'}
          detail={
            total
              ? [
                  `facturé HT ${total.facture.texte}`,
                  total.montantsInconnus > 0
                    ? pluriel(total.montantsInconnus, 'montant inconnu', 'montants inconnus')
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : undefined
          }
          ton="blue"
        />
        <Compteur
          libelle="Encaissé"
          valeur={total?.encaisse.texte ?? '—'}
          detail={
            total
              ? [
                  `reste dû ${total.resteDu.texte}`,
                  total.delaiMoyenReglement !== null ? texteDelai(total.delaiMoyenReglement) : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : undefined
          }
          ton="mint"
        />
        <Compteur
          libelle="En retard de paiement"
          valeur={total?.enRetard.montant.texte ?? '—'}
          detail={
            total
              ? total.enRetard.nombre === 0
                ? 'Aucune facture en retard'
                : detailRetard(total.enRetard)
              : undefined
          }
          ton={total && total.enRetard.nombre > 0 ? 'red' : 'navy'}
        />
      </div>

      <BarreFiltres
        onReinitialiser={filtreActif ? () => setFiltres(SANS_FILTRE) : null}
        onExporter={() => void exporter()}
        exportEnCours={exportEnCours}
      >
        <ChampRecherche
          valeur={filtres.recherche}
          onChange={(valeur) => changer('recherche', valeur)}
          etiquette="Rechercher un dossier"
          placeholder="Apprenant, employeur, référence…"
        />
        <ChampFiltre valeur={filtres.financeur} onChange={(valeur) => changer('financeur', valeur)} etiquette="Financeur">
          <option value="">Tous les financeurs</option>
          {data?.filtres.financeurs.map((option) => (
            <option key={option.valeur} value={option.valeur}>
              {option.typeLibelle ? `${option.libelle} · ${option.typeLibelle}` : option.libelle}
            </option>
          ))}
        </ChampFiltre>
        <ChampFiltre valeur={filtres.type} onChange={(valeur) => changer('type', valeur)} etiquette="Type de financeur">
          <option value="">Tous les types</option>
          {data?.filtres.types.map((option) => (
            <option key={option.valeur} value={option.valeur}>
              {option.libelle}
            </option>
          ))}
        </ChampFiltre>
        <ChampFiltre
          valeur={filtres.typeFacturation}
          onChange={(valeur) => changer('typeFacturation', valeur)}
          etiquette="Type de facturation"
        >
          <option value="">Toutes les facturations</option>
          {data?.filtres.typesFacturation.map((option) => (
            <option key={option.valeur} value={option.valeur}>
              {option.libelle}
            </option>
          ))}
        </ChampFiltre>
        <ChampFiltre valeur={filtres.statut} onChange={(valeur) => changer('statut', valeur)} etiquette="Statut du dossier">
          <option value="">Tous les statuts</option>
          {data?.filtres.statuts.map((option) => (
            <option key={option.valeur} value={option.valeur}>
              {option.libelle}
            </option>
          ))}
        </ChampFiltre>
        <ChampFiltre valeur={filtres.retard} onChange={(valeur) => changer('retard', valeur)} etiquette="Retards">
          <option value="">Tous les dossiers</option>
          {data?.filtres.retards.map((option) => (
            <option key={option.valeur} value={option.valeur}>
              {option.libelle}
            </option>
          ))}
        </ChampFiltre>
      </BarreFiltres>

      {error && (
        <MessageErreur
          message={error instanceof ErreurApi ? error.message : 'Le pilotage par financeur n’a pas pu être chargé.'}
        />
      )}

      <Carte titre="Par financeur" sansBordure>
        {isLoading && <Chargement libelle="Chargement du pilotage…" />}
        {vide && (
          <p className="px-4 py-12 text-center text-sm text-codialis-gray sm:px-6">
            Aucun contrat d’apprentissage validé ni accord de financement OF dans ce périmètre.
          </p>
        )}
        {data && !vide && data.financeurs.length === 0 && (
          <p className="px-4 py-12 text-center text-sm text-codialis-gray sm:px-6">
            Aucun dossier ne correspond aux filtres actifs.
          </p>
        )}
        {data && data.financeurs.length > 0 && (
          <>
            <ul className="divide-y divide-gray-100 md:hidden">
              {data.financeurs.map((ligne) => (
                <li key={ligne.cle} className="space-y-3 px-4 py-4">
                  <EnteteFinanceur ligne={ligne} />
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-xs">
                    <Valeur libelle="Dossiers · réf. DK">
                      <Dossiers ligne={ligne} />
                    </Valeur>
                    <Valeur libelle="Pris en charge">
                      <PrisEnCharge ligne={ligne} />
                    </Valeur>
                    <Valeur libelle="Facturé · encaissé">
                      <FactureEncaisse ligne={ligne} />
                    </Valeur>
                    <Valeur libelle="Reste dû">
                      <ResteDu ligne={ligne} />
                    </Valeur>
                  </dl>
                  {filtres.financeur !== ligne.cle && (
                    <button type="button" onClick={() => voirDossiers(ligne.cle)} className={CLASSE_LIEN}>
                      Voir ses dossiers
                    </button>
                  )}
                </li>
              ))}
              {data.financeurs.length > 1 && (
                <li className="space-y-1 bg-codialis-sand px-4 py-4 text-xs">
                  <div className="font-display text-sm font-bold text-codialis-navy">Ensemble</div>
                  <div className="text-codialis-gray">
                    {pluriel(data.total.dossiers.total, 'dossier')} · pris en charge {data.total.engage?.texte ?? '—'} ·
                    reste dû {data.total.resteDu.texte}
                  </div>
                </li>
              )}
            </ul>

            <div className="relative hidden overflow-x-auto md:block">
              <table className="w-full min-w-[680px] text-sm">
                <thead className="border-b border-gray-100 bg-gray-50">
                  <tr>
                    <th className={`${CLASSE_TH} text-left`}>Financeur</th>
                    <th className={`${CLASSE_TH} text-left`}>Dossiers · réf. DK</th>
                    <th className={`${CLASSE_TH} text-right`}>Pris en charge</th>
                    <th className={`${CLASSE_TH} text-right`}>Facturé · encaissé</th>
                    <th className={`${CLASSE_TH} text-right`}>Reste dû</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {data.financeurs.map((ligne) => (
                    <tr key={ligne.cle} className="transition hover:bg-gray-50">
                      <td className={`${CLASSE_TD} max-w-[220px]`}>
                        <EnteteFinanceur ligne={ligne} />
                        {filtres.financeur !== ligne.cle && (
                          <button type="button" onClick={() => voirDossiers(ligne.cle)} className={CLASSE_LIEN}>
                            Voir ses dossiers
                          </button>
                        )}
                      </td>
                      <td className={CLASSE_TD}>
                        <Dossiers ligne={ligne} />
                      </td>
                      <td className={`${CLASSE_TD} text-right`}>
                        <PrisEnCharge ligne={ligne} />
                      </td>
                      <td className={`${CLASSE_TD} text-right`}>
                        <FactureEncaisse ligne={ligne} />
                      </td>
                      <td className={`${CLASSE_TD} text-right`}>
                        <ResteDu ligne={ligne} />
                      </td>
                    </tr>
                  ))}
                </tbody>
                {data.financeurs.length > 1 && (
                  <tfoot className="border-t-2 border-gray-100 bg-codialis-sand">
                    <tr>
                      <td className={`${CLASSE_TD} font-display font-bold text-codialis-navy`}>Ensemble</td>
                      <td className={CLASSE_TD}>
                        <Dossiers ligne={data.total} />
                      </td>
                      <td className={`${CLASSE_TD} text-right`}>
                        <PrisEnCharge ligne={data.total} />
                      </td>
                      <td className={`${CLASSE_TD} text-right`}>
                        <FactureEncaisse ligne={data.total} />
                      </td>
                      <td className={`${CLASSE_TD} text-right`}>
                        <ResteDu ligne={data.total} />
                      </td>
                    </tr>
                  </tfoot>
                )}
              </table>
            </div>
          </>
        )}
      </Carte>

      <div ref={refDossiers} className="scroll-mt-4">
        <Carte titre={data ? `Dossiers · ${dossiers.length}` : 'Dossiers'} sansBordure>
          {data && dossiers.length === 0 && !vide && (
            <p className="px-4 py-12 text-center text-sm text-codialis-gray sm:px-6">
              Aucun dossier ne correspond aux filtres actifs.
            </p>
          )}
          {affiches.length > 0 && (
            <>
              {/* Une ligne en trois blocs jusqu'au grand écran : le tableau y serait trop serré. */}
              <ul className="divide-y divide-gray-100 xl:hidden">
                {affiches.map((ligne) => (
                  <li
                    key={ligne.cle}
                    className="space-y-3 px-4 py-4 sm:px-6 md:grid md:grid-cols-3 md:gap-5 md:space-y-0"
                  >
                    <div className="min-w-0 space-y-1">
                      <LienDossier ligne={ligne} />
                      <div className="text-xs text-codialis-gray">{ligne.formation}</div>
                      <div className="flex flex-wrap items-center gap-2">
                        <PastilleSite site={ligne.siteCode} />
                      </div>
                      <StatutDuDossier ligne={ligne} />
                    </div>
                    <FinanceurDuDossier ligne={ligne} />
                    <div className="min-w-0 space-y-2">
                      <MontantsDuDossier ligne={ligne} />
                      <RetardsDuDossier ligne={ligne} />
                      <Link to={`/adm-dossier/${ligne.apprenant.id}`} className={CLASSE_LIEN}>
                        Ouvrir le dossier
                      </Link>
                    </div>
                  </li>
                ))}
              </ul>

              <div className="relative hidden overflow-x-auto xl:block">
                <table className="w-full min-w-[900px] text-sm">
                  <thead className="border-b border-gray-100 bg-gray-50">
                    <tr>
                      <th className={`${CLASSE_TH} text-left`}>Apprenant</th>
                      <th className={`${CLASSE_TH} text-left`}>Financeur · référence</th>
                      <th className={`${CLASSE_TH} text-left`}>Statut</th>
                      <th className={`${CLASSE_TH} text-right`}>Montants</th>
                      <th className={`${CLASSE_TH} text-left`}>Retards</th>
                      <th className={CLASSE_TH}>
                        <span className="sr-only">Actions</span>
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {affiches.map((ligne) => (
                      <tr key={ligne.cle} className="transition hover:bg-gray-50">
                        <td className={CLASSE_TD}>
                          <LienDossier ligne={ligne} />
                          <div className="text-xs text-codialis-gray">{ligne.formation}</div>
                          <div className="mt-1.5">
                            <PastilleSite site={ligne.siteCode} />
                          </div>
                        </td>
                        <td className={`${CLASSE_TD} max-w-[220px]`}>
                          <FinanceurDuDossier ligne={ligne} />
                        </td>
                        <td className={CLASSE_TD}>
                          <StatutDuDossier ligne={ligne} />
                        </td>
                        <td className={`${CLASSE_TD} text-right`}>
                          <MontantsDuDossier ligne={ligne} />
                        </td>
                        <td className={`${CLASSE_TD} max-w-[220px]`}>
                          <RetardsDuDossier ligne={ligne} />
                        </td>
                        <td className={`${CLASSE_TD} text-right`}>
                          <Link to={`/adm-dossier/${ligne.apprenant.id}`} className={CLASSE_LIEN}>
                            Ouvrir le dossier
                          </Link>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <Pagination
                page={page}
                pages={pages}
                total={dossiers.length}
                affiches={affiches.length}
                taillePage={taillePage}
                onPage={setPage}
                onChangerTaille={setTaillePage}
                uniteLabel="dossier"
              />
            </>
          )}
        </Carte>
      </div>

      {data && data.perimetre.parcoursSansAccord > 0 && (
        <p className="text-xs text-codialis-gray">
          {data.perimetre.parcoursSansAccord > 1
            ? `${data.perimetre.parcoursSansAccord} parcours OF n’ont pas d’accord de financement : sans financeur, ils ne figurent pas ici.`
            : '1 parcours OF n’a pas d’accord de financement : sans financeur, il ne figure pas ici.'}
        </p>
      )}
    </>
  );
}

function Valeur({ libelle, children }: { libelle: string; children: ReactNode }): JSX.Element {
  return (
    <div className="min-w-0">
      <dt className="text-[10px] font-bold uppercase tracking-wider text-codialis-gray">{libelle}</dt>
      <dd className="mt-0.5">{children}</dd>
    </div>
  );
}

function EnteteFinanceur({ ligne }: { ligne: SyntheseFinanceur }): JSX.Element {
  return (
    <div className="min-w-0 space-y-1">
      <div className="break-words font-semibold text-codialis-navy">{ligne.nom}</div>
      {ligne.typeLibelle ? (
        <Pastille texte={ligne.typeLibelle} ton={ligne.type === 'ENTREPRISE' ? 'neutral' : 'info'} />
      ) : (
        <p className="text-[11px] font-semibold text-amber-700">Payeur à saisir au bloc Financement</p>
      )}
    </div>
  );
}

function Dossiers({ ligne }: { ligne: SyntheseFinanceur }): JSX.Element {
  const autres = autresStatuts(ligne.dossiers.parStatut);
  return (
    <div className="space-y-0.5">
      <div className="text-codialis-navy">
        <span className="font-semibold tabular-nums">{ligne.dossiers.parStatut.EN_COURS}</span> en cours
        <span className="text-codialis-gray"> / {ligne.dossiers.total}</span>
      </div>
      {autres && <div className="text-[11px] text-codialis-gray">{autres}</div>}
      <div className="text-[11px] text-codialis-gray">
        {pluriel(ligne.references.saisies, 'réf. DK saisie', 'réf. DK saisies')}
        {ligne.references.manquantes > 0 && (
          <span className="font-semibold text-amber-700"> · {pluriel(ligne.references.manquantes, 'manquante')}</span>
        )}
      </div>
    </div>
  );
}

function PrisEnCharge({ ligne }: { ligne: SyntheseFinanceur }): JSX.Element {
  return (
    <div className="space-y-0.5">
      <div className="whitespace-nowrap font-semibold tabular-nums text-codialis-navy">{ligne.engage?.texte ?? '—'}</div>
      {ligne.montantsInconnus > 0 && (
        <div className="text-[11px] text-amber-700">
          + {pluriel(ligne.montantsInconnus, 'montant inconnu', 'montants inconnus')}
        </div>
      )}
      {ligne.nonFacturable && (
        <div className="text-[11px] text-codialis-gray">dont {ligne.nonFacturable.texte} non facturable (rupture)</div>
      )}
      {ligne.resteAFacturer && (
        <div className="text-[11px] text-codialis-gray">reste à facturer {ligne.resteAFacturer.texte}</div>
      )}
      {ligne.depassement && (
        <div className="text-[11px] font-semibold text-red-700">facturé au-delà : {ligne.depassement.texte}</div>
      )}
      {ligne.aFacturer > 0 && (
        <div className="text-[11px] font-semibold text-amber-700">
          {ligne.aFacturer > 1 ? `${ligne.aFacturer} à facturer, échus` : '1 à facturer, échu'}
        </div>
      )}
    </div>
  );
}

function FactureEncaisse({ ligne }: { ligne: SyntheseFinanceur }): JSX.Element {
  return (
    <div className="space-y-0.5 tabular-nums">
      <div className="whitespace-nowrap text-codialis-navy">{ligne.facture.texte}</div>
      <div className="text-[11px] text-codialis-gray">encaissé {ligne.encaisse.texte}</div>
    </div>
  );
}

function ResteDu({ ligne }: { ligne: SyntheseFinanceur }): JSX.Element {
  return (
    <div className="space-y-0.5">
      <div className="whitespace-nowrap font-semibold tabular-nums text-codialis-navy">{ligne.resteDu.texte}</div>
      {ligne.enRetard.nombre > 0 && (
        <>
          <div className="text-[11px] font-semibold text-red-700">{ligne.enRetard.montant.texte} en retard</div>
          <div className="text-[11px] text-red-700">{detailRetard(ligne.enRetard)}</div>
        </>
      )}
      {ligne.delaiMoyenReglement !== null && (
        <div className="text-[11px] text-codialis-gray">{texteDelai(ligne.delaiMoyenReglement)}</div>
      )}
    </div>
  );
}

function LienDossier({ ligne }: { ligne: DossierPilote }): JSX.Element {
  return (
    <Link
      to={`/adm-dossier/${ligne.apprenant.id}`}
      className="font-semibold text-codialis-navy hover:text-codialis-sky hover:underline"
    >
      {ligne.apprenant.prenom} {ligne.apprenant.nom}
    </Link>
  );
}

function FinanceurDuDossier({ ligne }: { ligne: DossierPilote }): JSX.Element {
  return (
    <div className="min-w-0 space-y-0.5 text-sm">
      <div className="flex flex-wrap items-center gap-1.5">
        <span className="break-words text-codialis-navy">{ligne.client?.nom ?? 'Payeur non renseigné'}</span>
        {!ligne.payeurActuel && <Pastille texte="Ancien payeur" ton="neutral" />}
      </div>
      {ligne.payeurActuel &&
        (ligne.reference ? (
          <div className="break-all font-mono text-[11px] text-codialis-gray">{ligne.reference}</div>
        ) : (
          <div className={`text-[11px] ${ligne.referenceManquante ? 'font-semibold text-amber-700' : 'text-codialis-gray'}`}>
            {ligne.referenceManquante ? 'Référence DK manquante' : 'Sans référence'}
          </div>
        ))}
      {!ligne.payeurActuel && (
        <div className="text-[11px] text-codialis-gray">Factures émises avant le changement de payeur</div>
      )}
      {ligne.employeur && ligne.employeur !== ligne.client?.nom && (
        <div className="break-words text-[11px] text-codialis-gray">Employeur : {ligne.employeur}</div>
      )}
    </div>
  );
}

function StatutDuDossier({ ligne }: { ligne: DossierPilote }): JSX.Element {
  return (
    <div className="space-y-1">
      <Pastille texte={ligne.statut.libelle} ton={ligne.statut.ton} />
      {ligne.dateDebut && ligne.dateFin && (
        <div className="whitespace-nowrap text-[11px] text-codialis-gray">
          {jourFrancais(ligne.dateDebut)} → {jourFrancais(ligne.dateFin)}
        </div>
      )}
      <div className="text-[11px] text-codialis-gray">{ligne.typeLibelle ?? 'Parcours OF'}</div>
    </div>
  );
}

function MontantsDuDossier({ ligne }: { ligne: DossierPilote }): JSX.Element {
  const lignes: [string, string][] = [
    ...(ligne.payeurActuel ? [['Pris en charge', ligne.engage?.texte ?? 'inconnu'] as [string, string]] : []),
    ...(ligne.nonFacturable ? [['Non facturable (rupture)', ligne.nonFacturable.texte] as [string, string]] : []),
    ['Facturé HT', ligne.facture.texte],
    ['Encaissé', ligne.encaisse.texte],
    ['Reste dû', ligne.resteDu.texte],
  ];
  return (
    <dl className="space-y-0.5 text-[11px] tabular-nums xl:text-right">
      {lignes.map(([libelle, valeur]) => (
        <div key={libelle} className="flex justify-between gap-3 xl:justify-end">
          <dt className="whitespace-nowrap text-codialis-gray">{libelle}</dt>
          <dd className="whitespace-nowrap font-semibold text-codialis-navy">{valeur}</dd>
        </div>
      ))}
    </dl>
  );
}

function RetardsDuDossier({ ligne }: { ligne: DossierPilote }): JSX.Element {
  const retard = ligne.enRetard.nombre > 0;
  return (
    <div className="space-y-0.5 text-[11px]">
      {retard && (
        <p className="font-semibold text-red-700">
          {ligne.enRetard.montant.texte} en retard · {detailRetard(ligne.enRetard)}
        </p>
      )}
      {ligne.aFacturer.texte && <p className="font-semibold text-amber-700">{ligne.aFacturer.texte}</p>}
      {ligne.depassement && <p className="font-semibold text-red-700">Facturé au-delà : {ligne.depassement.texte}</p>}
      {!retard && !ligne.aFacturer.texte && !ligne.depassement && <p className="text-codialis-gray">Aucun retard</p>}
      {ligne.resteAFacturer && ligne.resteAFacturer.centimes > 0 && (
        <p className="text-codialis-gray">Reste à facturer : {ligne.resteAFacturer.texte}</p>
      )}
    </div>
  );
}
